import * as React from "react"
import { createContext, useContext, useState, useEffect, useCallback } from "react"
import type { OvalClubSettings } from "@/lib/ovalClubSettings"
import { createDealsApi } from "@/contexts/auth-api/deals"
import { createChatApi } from "@/contexts/auth-api/chat"
import { createAdminApi } from "@/contexts/auth-api/admin"
import { createDashboardApi } from "@/contexts/auth-api/dashboard"
import { createProfileApi } from "@/contexts/auth-api/profile"
import { createStreaksApi } from "@/contexts/auth-api/streaks"
import type { AgentInfo, Deal, DealSlotReward } from "@/contexts/auth-api/deal-types"
import type { ChatMessage } from "@/contexts/auth-api/chat-types"
import type {
  AdminStreakEntry,
  ClaimStreakResult,
  CreateStreakData,
  StreakAnalytics,
  StreakHistoryDay,
  UserStreakEntry,
} from "@/contexts/auth-api/streak-types"

interface WelcomeReward {
  amount: number
  wonAt: string | null
  status: "pending" | "claimed" | "expired"
  claimDeadline: string | null
}

interface Balance {
  pending: number
  available: number
}

interface Points {
  current: number
  lifetime: number
  weeklyDeals: number
  monthlyDeals: number
  totalDeals: number
}

export interface SideQuest {
  questId: string
  tier: "easy" | "medium" | "hard"
  label: string
  target: number
  cashPrize: number
  progress: number
  assignedAt: string
  deadline: string
  status: "active" | "completed" | "expired"
  completedAt: string | null
}

function normalizeDealSlotRewardPayload(raw: any): DealSlotReward | undefined {
  if (!raw || typeof raw !== "object") return undefined

  const reward = raw as DealSlotReward
  if (reward.type !== "bonus") return reward

  const hasMain = typeof reward.mainGameAmount === "number" && Number.isFinite(reward.mainGameAmount)
  const hasBonus = typeof reward.bonusGameAmount === "number" && Number.isFinite(reward.bonusGameAmount)
  const hasTotal = typeof reward.totalCashAmount === "number" && Number.isFinite(reward.totalCashAmount)

  if (!hasMain && !hasBonus && !hasTotal) return reward

  const totalCashAmount = hasTotal
    ? Math.max(0, reward.totalCashAmount!)
    : Math.max(0, (hasMain ? reward.mainGameAmount! : reward.amount) + (hasBonus ? reward.bonusGameAmount! : 0))

  return { ...reward, totalCashAmount }
}

export interface LeaderboardEntry {
  rank: number
  name: string
  deals: number
  earnings: number
  isCurrentUser: boolean
  imageUrl?: string | null
}

export interface LeaderboardData {
  leaderboard: LeaderboardEntry[]
  currentUserPosition: {
    rank: number
    name: string
    deals: number
    earnings: number
  } | null
  mode: "agency" | "agent"
}

export interface AgencyStats {
  memberCount: number
  dealsToday: number
  dealsThisWeek: number
  onlineCount: number
}

export type UserRole = "user" | "super_admin" | "support"

export interface AdminPermissions {
  canViewAdminDashboard: boolean
  canManageUsers: boolean
  canModerateContent: boolean
  canManageSystemSettings: boolean
}

export interface AdminAgencyTarget {
  agencyId: string
  agencyName: string
}

export interface InvestigationSession {
  isActive: boolean
  mode: "investigate"
  actorUserId: string
  actorRole: UserRole
  actorEmail: string
}

export interface AdminCustomerSearchResult {
  _id: string
  email: string
  displayName: string
  agencyName: string | null
  userType: "agent" | "solo_agent" | "agency" | null
  hasCompletedOnboarding: boolean
  emailVerified: boolean
  createdAt: string
}

export interface WeeklyRaffleWinner {
  displayName: string
  prizeLabel: string
  cashUsd: number
  isCurrentUser: boolean
}

export interface WeeklyRaffleStatus {
  success: boolean
  status: "pending" | "drawn"
  totalQualified: number
  drawTime?: string
  currentUserQualified?: boolean
  qualifiedUsers?: string[]
  winners?: WeeklyRaffleWinner[]
  drawDate?: string
  nextDrawTime?: string
  message?: string
}

interface User {
  _id: string
  email: string
  role: UserRole
  emailVerified: boolean
  hasCompletedOnboarding: boolean
  onboardingData: any | null
  welcomeReward: WelcomeReward | null
  balance: Balance
  points: Points
  generatedAgencyId: string | null
  profilePicture: string | null
  freeSpins?: number
  sideQuests?: SideQuest[]
}

export interface AdminOverviewMetrics {
  totalUsers: number
  totalDeals: number
  activeAgencies: number
  pendingApprovals: number
  estimatedRevenue: number
  newUsersThisWeek: number
  approvalRate: number
  avgDealsPerAgent: number
}

export interface AdminDealVolumeEntry {
  date: string
  approved: number
  pending: number
  rejected: number
}

export interface AdminPendingDeal {
  _id: string
  memberId: string
  submitterName: string
  submitterEmail: string
  createdAt: string
  pointsEarned: number
}

export interface AdminAgentPerformanceEntry {
  _id: string
  name: string
  email: string
  agencyName: string
  weeklyDeals: number
  dailyDeals: number
  monthlyDeals: number
  totalDeals: number
  points: number
}

export interface AdminAgencyInfo {
  agencyId: string
  name: string
  ownerEmail: string
  memberCount: number
  monthlyDeals: number
  dailyDeals: number
  totalDeals: number
}

export interface AdminAgenciesPaginatedResult {
  success: boolean
  agencies?: AdminAgencyInfo[]
  total?: number
  page?: number
  limit?: number
  totalPages?: number
  message?: string
}

export interface AdminFilterOptions {
  agents: Array<{ _id: string; name: string; email: string }>
  agencies: Array<{ agencyId: string; agencyName: string }>
  states: string[]
  userTypes: string[]
}

export interface AdminChatActivityData {
  byAgency: Array<{ agencyId: string; agencyName: string; messageCount: number }>
  dailyVolume: Array<{ date: string; count: number }>
}

export interface AdminChatControlState {
  systemOnlyMode: boolean
  systemOnlyUntil: string | null
  reason: string | null
}

export interface AmaEvent {
  _id: string
  title: string
  description: string
  hostName: string
  startsAt: string
  durationMinutes: number
  joinUrl: string
  streamUrl: string | null
  sendToAll: boolean
  agencyId: string | null
  agencyName: string | null
  createdByName: string
  createdAt: string
}

// ─── Game Events ──────────────────────────────────────────────────────────────

export interface GameEvent {
  _id: string
  title: string
  description: string
  slug: string
  eventType: "action_challenge" | "points_multiplier" | "raffle" | "king_of_hill" | "custom"
  status: "draft" | "coming_soon" | "live" | "ended"
  isFeatured: boolean
  startDate: string | null
  endDate: string | null
  imageUrl: string | null
  detailImageUrl: string | null
  rewards: Array<{ type: string; amount: number; label: string }>
  actionRequirements: Array<{ type: string; target: number; metric: string }>
  pointsMultiplier: number | null
  badge: string
  badgeColor: string
  iconName: string
  participantCount: number
  createdAt: string
}

export interface GameEventProgress {
  _id: string
  gameEventId: string
  status: "active" | "completed" | "failed"
  requirementProgress: Array<{
    requirementIndex: number
    current: number
    target: number
    completed: boolean
    completedAt: string | null
  }>
  rewardsClaimed: boolean
  joinedAt: string
  completedAt: string | null
}

export interface GameEventWithProgress extends GameEvent {
  progress?: GameEventProgress | null
}

export interface AdminAdjustedUser {
  _id: string
  email: string
  pointsCurrent: number
  pointsLifetime: number
  bonusPending: number
  bonusAvailable: number
}

export interface StoreProduct {
  _id: string
  name: string
  description: string
  category: string
  sku: string
  badge: string
  priceUsd: number
  stock: number
  imageUrls: string[]
  primaryImageUrl: string | null
  inStock: boolean
  isActive: boolean
  featured: boolean
  maxPerOrder: number | null
  createdAt: string
  updatedAt: string
}


export interface StoreOrder {
  _id: string
  orderNumber: string
  status: "mock_paid"
  paymentMethod: "mock"
  subtotalUsd: number
  totalUsd: number
  notes: string
  createdAt: string
  items: Array<{
    productId: string
    productName: string
    sku: string
    imageUrl: string | null
    quantity: number
    unitPriceUsd: number
    lineTotalUsd: number
  }>
  shipping: {
    fullName: string
    email: string
    phone: string
    addressLine1: string
    addressLine2: string
    city: string
    state: string
    postalCode: string
    country: string
  }
}

interface AuthContextType {
  user: User | null
  token: string | null
  investigation: InvestigationSession | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; requiresVerification?: boolean; requiresPhoneVerification?: boolean; email?: string; phoneNumber?: string }>
  signup: (email: string, password: string, phoneNumber: string) => Promise<{ success: boolean; message?: string; requiresVerification?: boolean; requiresPhoneVerification?: boolean; email?: string; phoneNumber?: string }>
  verifyEmail: (email: string, code: string) => Promise<{ success: boolean; message?: string; requiresPhoneVerification?: boolean; email?: string; phoneNumber?: string }>
  verifyPhone: (email: string, code: string) => Promise<{ success: boolean; message?: string; requiresPhoneVerification?: boolean; email?: string; phoneNumber?: string }>
  resendVerificationCode: (email: string) => Promise<{ success: boolean; message?: string }>
  resendPhoneVerificationCode: (email: string) => Promise<{ success: boolean; message?: string }>
  logout: () => void
  completeOnboarding: (onboardingData: any, generatedAgencyId?: string) => Promise<{ success: boolean; message?: string }>
  validateAgencyId: (agencyId: string) => Promise<{ success: boolean; message?: string; agencyName?: string }>
  checkAgencyName: (name: string) => Promise<{ available: boolean }>
  getWelcomeSpinConfig: () => Promise<{ success: boolean; message?: string; prizes?: Array<{ amount: number; label: string; probabilityPct: number }> }>
  spinWelcomeReward: () => Promise<{ success: boolean; message?: string; reward?: { amount: number; label: string } }>
  getCasinoSlotConfig: () => Promise<{ success: boolean; message?: string; slot?: { probabilities: Array<{ outcome: string; payoutMultiplier: number; probabilityPct: number }> } }>
  getActiveSlotSettings: () => Promise<{ success: boolean; message?: string; settings?: { outcomeTypes: Array<{ name: string; probability: number; enabled: boolean }>; outcomeRanges: Record<string, Array<{ value: number | string; probability: number; label: string; metadata?: Record<string, unknown> }>>; source: string; expiresAt?: string } }>
  claimWelcomeReward: (amount: number) => Promise<{ success: boolean; message?: string }>
  submitDeal: (memberId: string, onBehalfOfAgentId?: string) => Promise<{ success: boolean; message?: string; deal?: Deal }>
  claimDealSlotReward: (dealId: string) => Promise<{ success: boolean; message?: string; reward?: DealSlotReward; alreadyClaimed?: boolean }>
  claimFreeSpinReward: () => Promise<{ success: boolean; message?: string; reward?: DealSlotReward }>
  getDeals: () => Promise<{ success: boolean; deals?: Deal[]; message?: string }>
  getAgencyAgents: () => Promise<{ success: boolean; agents?: AgentInfo[]; message?: string }>
  getMessages: (agencyId: string) => Promise<{
    success: boolean
    messages?: ChatMessage[]
    isAgencyAdmin?: boolean
    currentUserMuted?: boolean
    mutedUserIds?: string[]
    chatSystemOnlyMode?: boolean
    chatSystemOnlyUntil?: string | null
    chatSystemOnlyReason?: string | null
    message?: string
  }>
  sendChatMessage: (
    agencyId: string,
    content: string,
    options?: {
      fileData?: { fileUrl: string; fileName: string; fileType: string }
      messageType?: "text" | "gif" | "sticker" | "poll"
      gifUrl?: string
      sticker?: string
      poll?: {
        question: string
        options: Array<{ id?: string; text: string; emoji?: string | null }>
        allowMultiSelect?: boolean
        closesAt?: string | null
      }
    }
  ) => Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }>
  reactToChatMessage: (agencyId: string, messageId: string, emoji: string) => Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }>
  editChatMessage: (agencyId: string, messageId: string, content: string) => Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }>
  pinChatMessage: (agencyId: string, messageId: string, pinned: boolean) => Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }>
  deleteChatMessage: (agencyId: string, messageId: string) => Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }>
  muteChatUser: (agencyId: string, targetUserId: string, muted: boolean) => Promise<{ success: boolean; mutedUserIds?: string[]; message?: string }>
  voteChatPoll: (agencyId: string, messageId: string, optionId: string) => Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }>
  getAdminSession: () => Promise<{
    success: boolean
    role?: UserRole
    permissions?: AdminPermissions
    message?: string
  }>
  getAdminAgencyTargets: () => Promise<{
    success: boolean
    agencies?: AdminAgencyTarget[]
    message?: string
  }>
  searchAdminUsers: (query: string) => Promise<{
    success: boolean
    users?: AdminCustomerSearchResult[]
    message?: string
  }>
  startInvestigateMode: (targetUserId: string) => Promise<{
    success: boolean
    message?: string
  }>
  endInvestigateMode: () => Promise<{
    success: boolean
    message?: string
  }>
  postAdminSystemChatMessage: (payload: {
    content: string
    sendToAll: boolean
    agencyId?: string
    fileUrl?: string
    fileType?: string
    fileName?: string
    ctas?: Array<{ label: string; url: string }>
  }) => Promise<{
    success: boolean
    message?: string
    postedCount?: number
    agencyIds?: string[]
  }>
  getPresignedUrl: (fileName: string, fileType: string) => Promise<{ success: boolean; uploadUrl?: string; fileUrl?: string; message?: string }>
  getLeaderboard: (mode?: "agency" | "agent") => Promise<{ success: boolean; data?: LeaderboardData; message?: string }>
  getAgencyStats: (agencyId: string) => Promise<{ success: boolean; stats?: AgencyStats; message?: string }>
  getAmaEvents: () => Promise<{ success: boolean; events?: AmaEvent[]; message?: string }>
  getStoreProducts: () => Promise<{ success: boolean; products?: StoreProduct[]; message?: string }>
  getMyStoreOrders: () => Promise<{ success: boolean; orders?: StoreOrder[]; message?: string }>
  mockStoreCheckout: (payload: {
    items: Array<{ productId: string; quantity: number }>
    shipping: {
      fullName: string
      email: string
      phone?: string
      addressLine1: string
      addressLine2?: string
      city: string
      state: string
      postalCode: string
      country: string
    }
    notes?: string
  }) => Promise<{ success: boolean; order?: StoreOrder; message?: string }>
  createAdminAmaEvent: (payload: {
    title: string
    description?: string
    hostName: string
    startsAt: string
    durationMinutes?: number
    joinUrl: string
    streamUrl?: string
    sendToAll?: boolean
    agencyId?: string
  }) => Promise<{ success: boolean; event?: AmaEvent; postedCount?: number; message?: string }>
  getProfilePicturePresignedUrl: (fileName: string, fileType: string) => Promise<{ success: boolean; uploadUrl?: string; fileUrl?: string; message?: string }>
  updateProfilePicture: (profilePictureUrl: string) => Promise<{ success: boolean; message?: string }>
  refreshUser: () => Promise<void>
  getAdminOverview: (startDate?: string, endDate?: string, agentId?: string, agencyId?: string, state?: string, userType?: string) => Promise<{ success: boolean; metrics?: AdminOverviewMetrics; message?: string }>
  getAdminFilterOptions: () => Promise<{ success: boolean; agents?: AdminFilterOptions["agents"]; agencies?: AdminFilterOptions["agencies"]; states?: string[]; userTypes?: string[]; message?: string }>
  getAdminDealVolume: (days?: number, agentId?: string, agencyId?: string, state?: string, userType?: string) => Promise<{ success: boolean; data?: AdminDealVolumeEntry[]; message?: string }>
  getAdminDealAnalytics: () => Promise<{ success: boolean; data?: any; message?: string }>
  getAdminPendingDeals: () => Promise<{ success: boolean; deals?: AdminPendingDeal[]; message?: string }>
  approveOrRejectDeal: (dealId: string, action: "approve" | "reject") => Promise<{ success: boolean; message?: string }>
  getAdminAgentPerformance: () => Promise<{ success: boolean; agents?: AdminAgentPerformanceEntry[]; message?: string }>
  getAdminAgencyOverview: () => Promise<{ success: boolean; agencies?: AdminAgencyInfo[]; message?: string }>
  getAdminAgenciesPaginated: (params: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: "asc" | "desc" }) => Promise<AdminAgenciesPaginatedResult>
  getAdminChatActivity: () => Promise<{ success: boolean; data?: AdminChatActivityData; message?: string }>
  getAdminChatControl: () => Promise<{ success: boolean; control?: AdminChatControlState; message?: string }>
  setAdminChatControl: (payload: {
    systemOnlyMode: boolean
    durationMinutes?: number
    systemOnlyUntil?: string
    reason?: string
  }) => Promise<{ success: boolean; control?: AdminChatControlState; message?: string }>
  adjustAdminUserEconomics: (payload: {
    email: string
    pointsDelta?: number
    bonusPendingDelta?: number
    bonusAvailableDelta?: number
    note?: string
  }) => Promise<{
    success: boolean
    user?: AdminAdjustedUser
    applied?: {
      pointsDelta: number
      bonusPendingDelta: number
      bonusAvailableDelta: number
      note: string | null
    }
    message?: string
  }>
  getAdminEconomicsSettings: () => Promise<{
    success: boolean
    settings?: OvalClubSettings
    updatedAt?: string
    updatedBy?: string | null
    message?: string
  }>
  updateAdminEconomicsSettings: (settings: OvalClubSettings) => Promise<{
    success: boolean
    settings?: OvalClubSettings
    updatedAt?: string
    updatedBy?: string | null
    message?: string
  }>
  getAdminStoreProducts: () => Promise<{ success: boolean; products?: StoreProduct[]; message?: string }>
  saveAdminStoreProduct: (payload: {
    _id?: string
    name: string
    description?: string
    category?: string
    sku?: string
    badge?: string
    priceUsd: number
    stock: number
    imageUrls?: string[]
    isActive?: boolean
    featured?: boolean
    maxPerOrder?: number | null
  }) => Promise<{ success: boolean; product?: StoreProduct; message?: string }>
  // Streak Admin
  getAdminStreaks: () => Promise<{ success: boolean; streaks?: AdminStreakEntry[]; message?: string }>
  createAdminStreak: (data: CreateStreakData) => Promise<{ success: boolean; streak?: any; message?: string }>
  updateAdminStreak: (streakId: string, data: Partial<CreateStreakData>) => Promise<{ success: boolean; streak?: any; message?: string }>
  deleteAdminStreak: (streakId: string) => Promise<{ success: boolean; message?: string }>
  getAdminStreakAnalytics: (streakId: string) => Promise<{ success: boolean; analytics?: StreakAnalytics; message?: string }>
  // User Streak Methods
  getUserStreaks: () => Promise<{ success: boolean; streaks?: UserStreakEntry[]; message?: string }>
  claimStreak: (streakId: string) => Promise<{ success: boolean; result?: ClaimStreakResult; message?: string }>
  getStreakHistory: (streakId: string) => Promise<{ success: boolean; history?: StreakHistoryDay[]; currentStreak?: number; longestStreak?: number; message?: string }>
  markStreakSeen: (streakId: string) => Promise<{ success: boolean; message?: string }>

  getRecentDealFeed: () => Promise<{ success: boolean; deals?: Array<{ displayName: string; pointsEarned: number; createdAt: string }>; message?: string }>
  getPokerRooms: () => Promise<{ success: boolean; rooms?: any[]; message?: string }>
  createPokerRoom: (name: string, settings: {
    maxPlayers?: number
    smallBlind: number
    bigBlind: number
    minBuyIn: number
    maxBuyIn: number
  }) => Promise<{ success: boolean; room?: any; buyIn?: number; message?: string }>
  joinPokerRoom: (roomId: string) => Promise<{ success: boolean; buyIn?: number; message?: string }>
  leavePokerRoom: (roomId: string) => Promise<{ success: boolean; returnedChips?: number; message?: string }>
  addPokerBot: (roomId: string, difficulty: string) => Promise<{ success: boolean; bot?: any; message?: string }>
  removePokerBot: (roomId: string, seatIndex: number) => Promise<{ success: boolean; message?: string }>
  getWeeklyRaffleStatus: () => Promise<WeeklyRaffleStatus>
  // Game Events
  getGameEvents: () => Promise<{ success: boolean; events?: GameEventWithProgress[]; featuredEvent?: GameEventWithProgress | null; message?: string }>
  getGameEventDetail: (eventId: string) => Promise<{ success: boolean; event?: GameEventWithProgress; message?: string }>
  getAdminGameEvents: () => Promise<{ success: boolean; events?: GameEvent[]; message?: string }>
  createAdminGameEvent: (payload: Partial<GameEvent> & { eventType: string }) => Promise<{ success: boolean; event?: GameEvent; message?: string }>
  updateAdminGameEvent: (eventId: string, payload: Partial<GameEvent>) => Promise<{ success: boolean; event?: GameEvent; message?: string }>
  announceGameEvent: (eventId: string, opts?: { content?: string; fileUrl?: string; fileType?: string; fileName?: string }) => Promise<{ success: boolean; postedCount?: number; message?: string }>
  adminAirdropBalance: (payload: {
    amount: number
    currency?: "cash" | "points"
    target: "all" | "specific"
    emails?: string[]
    claimDeadlineHours?: number | null
    note?: string
  }) => Promise<{
    success: boolean
    message?: string
    airdrop?: {
      amount: number
      currency: "cash" | "points"
      target: "all" | "specific"
      usersAffected: number
      claimDeadlineHours: number | null
      note: string | null
    }
  }>
  getUserPendingAirdrops: () => Promise<{
    success: boolean
    message?: string
    airdrops?: Array<{
      _id: string
      currency: "cash" | "points"
      amount: number
      note: string
      claimDeadline: string | null
      createdAt: string
    }>
  }>
  claimAirdrop: (airdropId: string) => Promise<{
    success: boolean
    message?: string
    claimed?: { currency: "cash" | "points"; amount: number }
  }>
  // Redemptions
  getPaymentMethods: () => Promise<{
    success: boolean
    message?: string
    methods?: Array<{ method: "cashapp" | "venmo" | "zelle" | "paypal"; handle: string; label: string }>
    availableBalance?: number
  }>
  createRedemption: (amount: number, paymentMethod: "cashapp" | "venmo" | "zelle" | "paypal", paymentHandle: string) => Promise<{
    success: boolean
    message?: string
    redemption?: {
      _id: string
      amount: number
      paymentMethod: "cashapp" | "venmo" | "zelle" | "paypal"
      paymentHandle: string
      status: "pending" | "processing" | "completed" | "rejected"
      createdAt: string
    }
  }>
  getUserRedemptions: () => Promise<{
    success: boolean
    message?: string
    redemptions?: Array<{
      _id: string
      amount: number
      paymentMethod: "cashapp" | "venmo" | "zelle" | "paypal"
      paymentHandle: string
      status: "pending" | "processing" | "completed" | "rejected"
      rejectionReason?: string
      createdAt: string
      processedAt?: string
    }>
  }>
  getAdminRedemptions: (statusFilter?: "pending" | "processing" | "completed" | "rejected") => Promise<{
    success: boolean
    message?: string
    redemptions?: Array<{
      _id: string
      userId: string
      userEmail: string
      userDisplayName: string
      amount: number
      paymentMethod: "cashapp" | "venmo" | "zelle" | "paypal"
      paymentHandle: string
      status: "pending" | "processing" | "completed" | "rejected"
      rejectionReason?: string
      createdAt: string
      processedAt?: string
      processedByEmail?: string
    }>
  }>
  updateRedemptionStatus: (redemptionId: string, status: "pending" | "processing" | "completed" | "rejected", rejectionReason?: string) => Promise<{
    success: boolean
    message?: string
  }>
}

const AuthContext = createContext<AuthContextType | null>(null)

const TOKEN_KEY = "oval_auth_token"
const INVESTIGATE_ORIGIN_TOKEN_KEY = "oval_investigate_origin_token"

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [investigation, setInvestigation] = useState<InvestigationSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const saveToken = useCallback((newToken: string) => {
    localStorage.setItem(TOKEN_KEY, newToken)
    setToken(newToken)
  }, [])

  const clearToken = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
  }, [])

  const saveInvestigateOriginToken = useCallback((originToken: string) => {
    localStorage.setItem(INVESTIGATE_ORIGIN_TOKEN_KEY, originToken)
  }, [])

  const getInvestigateOriginToken = useCallback(() => localStorage.getItem(INVESTIGATE_ORIGIN_TOKEN_KEY), [])

  const clearInvestigateOriginToken = useCallback(() => {
    localStorage.removeItem(INVESTIGATE_ORIGIN_TOKEN_KEY)
  }, [])

  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem(TOKEN_KEY)
    if (!savedToken) {
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch("/api/auth/me", {
        headers: {
          Authorization: `Bearer ${savedToken}`,
        },
      })
      const data = await response.json()

      if (data.success && data.user) {
        setUser(data.user)
        setToken(savedToken)
        setInvestigation(data.investigation || null)
      } else {
        clearToken()
        clearInvestigateOriginToken()
        setUser(null)
        setInvestigation(null)
      }
    } catch (error) {
      console.error("Failed to refresh user:", error)
      clearToken()
      clearInvestigateOriginToken()
      setUser(null)
      setInvestigation(null)
    } finally {
      setIsLoading(false)
    }
  }, [clearInvestigateOriginToken, clearToken])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  const signup = async (email: string, password: string, phoneNumber: string) => {
    try {
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, phoneNumber }),
      })
      const data = await response.json()

      if (data.success && data.requiresVerification) {
        return { success: true, requiresVerification: true, email: data.email }
      }

      if (data.success && data.requiresPhoneVerification) {
        return {
          success: true,
          requiresPhoneVerification: true,
          email: data.email,
          phoneNumber: data.phoneNumber,
        }
      }

      if (data.success && data.token && data.user) {
        clearInvestigateOriginToken()
        saveToken(data.token)
        setUser(data.user)
        setInvestigation(data.investigation || null)
        return { success: true }
      }

      return { success: false, message: data.message || "Signup failed" }
    } catch (error) {
      console.error("Signup error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      })
      const data = await response.json()

      if (data.success && data.requiresVerification) {
        return { success: true, requiresVerification: true, email: data.email }
      }

      if (data.success && data.requiresPhoneVerification) {
        return {
          success: true,
          requiresPhoneVerification: true,
          email: data.email,
          phoneNumber: data.phoneNumber,
        }
      }

      if (data.success && data.token && data.user) {
        clearInvestigateOriginToken()
        saveToken(data.token)
        setUser(data.user)
        setInvestigation(data.investigation || null)
        return { success: true }
      }

      return { success: false, message: data.message || "Login failed" }
    } catch (error) {
      console.error("Login error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const verifyEmail = async (email: string, code: string) => {
    try {
      const response = await fetch("/api/auth/verify-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code }),
      })
      const data = await response.json()

      if (data.success && data.requiresPhoneVerification) {
        return {
          success: true,
          message: data.message,
          requiresPhoneVerification: true,
          email: data.email,
          phoneNumber: data.phoneNumber,
        }
      }

      if (data.success && data.token && data.user) {
        clearInvestigateOriginToken()
        saveToken(data.token)
        setUser(data.user)
        setInvestigation(data.investigation || null)
        return { success: true }
      }

      return { success: false, message: data.message || "Verification failed" }
    } catch (error) {
      console.error("Verify email error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const verifyPhone = async (email: string, code: string) => {
    try {
      const response = await fetch("/api/auth/verify-phone", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code }),
      })
      const data = await response.json()

      if (data.success && data.token && data.user) {
        clearInvestigateOriginToken()
        saveToken(data.token)
        setUser(data.user)
        setInvestigation(data.investigation || null)
        return { success: true }
      }

      return { success: false, message: data.message || "Phone verification failed" }
    } catch (error) {
      console.error("Verify phone error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const resendVerificationCode = async (email: string) => {
    try {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      })
      const data = await response.json()

      if (data.success) {
        return { success: true, message: data.message }
      }

      return { success: false, message: data.message || "Failed to resend code" }
    } catch (error) {
      console.error("Resend verification code error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const resendPhoneVerificationCode = async (email: string) => {
    try {
      const response = await fetch("/api/auth/resend-phone-verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      })
      const data = await response.json()

      if (data.success) {
        return { success: true, message: data.message }
      }

      return { success: false, message: data.message || "Failed to resend SMS code" }
    } catch (error) {
      console.error("Resend phone verification code error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const logout = () => {
    clearToken()
    clearInvestigateOriginToken()
    setUser(null)
    setInvestigation(null)
  }

  const completeOnboarding = async (onboardingData: any, generatedAgencyId?: string) => {
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await fetch("/api/auth/complete-onboarding", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ onboardingData, generatedAgencyId }),
      })
      const data = await response.json()

      if (data.success && data.user) {
        setUser(data.user)
        return { success: true }
      }

      return { success: false, message: data.message || "Failed to complete onboarding" }
    } catch (error) {
      console.error("Complete onboarding error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const validateAgencyId = async (agencyId: string) => {
    try {
      const response = await fetch("/api/auth/validate-agency-id", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ agencyId }),
      })
      const data = await response.json()

      return {
        success: data.success,
        message: data.message,
        agencyName: data.agencyName,
      }
    } catch (error) {
      console.error("Validate agency ID error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const checkAgencyName = async (name: string): Promise<{ available: boolean }> => {
    try {
      const response = await fetch("/api/auth/check-agency-name", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      })
      return await response.json()
    } catch {
      return { available: true } // Fail open — server-side will catch duplicates on submit
    }
  }

  const claimWelcomeReward = async (amount: number) => {
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await fetch("/api/auth/claim-welcome-reward", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount }),
      })
      const data = await response.json()

      if (data.success && data.user) {
        setUser(data.user)
        return { success: true }
      }

      return { success: false, message: data.message || "Failed to claim welcome reward" }
    } catch (error) {
      console.error("Claim welcome reward error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getWelcomeSpinConfig = async () => {
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await fetch("/api/auth/welcome-spin-config", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      const data = await response.json()
      return {
        success: Boolean(data.success),
        message: data.message,
        prizes: Array.isArray(data.prizes) ? data.prizes : undefined,
      }
    } catch (error) {
      console.error("Get welcome spin config error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const spinWelcomeReward = async () => {
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await fetch("/api/auth/spin-welcome-reward", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      const data = await response.json()
      return {
        success: Boolean(data.success),
        message: data.message,
        reward: data.reward && typeof data.reward.amount === "number"
          ? { amount: data.reward.amount, label: data.reward.label || "WIN!" }
          : undefined,
      }
    } catch (error) {
      console.error("Spin welcome reward error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const {
    submitDeal,
    claimDealSlotReward,
    claimFreeSpinReward,
    getDeals,
    getAgencyAgents,
    getRecentDealFeed,
    getCasinoSlotConfig,
  } = createDealsApi<User>({
    getToken: () => token,
    getGeneratedAgencyId: () => user?.generatedAgencyId,
    setUser,
    normalizeDealSlotRewardPayload,
  })

  const {
    getMessages,
    sendChatMessage,
    reactToChatMessage,
    editChatMessage,
    pinChatMessage,
    deleteChatMessage,
    muteChatUser,
    voteChatPoll,
    getPresignedUrl,
  } = createChatApi({
    getToken: () => token,
  })

  const {
    getAdminSession,
    getAdminAgencyTargets,
    searchAdminUsers,
    startInvestigateMode,
    endInvestigateMode,
    postAdminSystemChatMessage,
    createAdminAmaEvent,
    getAdminStoreProducts,
    saveAdminStoreProduct,
    getAdminFilterOptions,
    getAdminOverview,
    getAdminDealVolume,
    getAdminDealAnalytics,
    getAdminPendingDeals,
    approveOrRejectDeal,
    getAdminAgentPerformance,
    getAdminAgencyOverview,
    getAdminAgenciesPaginated,
    getAdminChatActivity,
    getAdminChatControl,
    setAdminChatControl,
    adjustAdminUserEconomics,
    getAdminEconomicsSettings,
    updateAdminEconomicsSettings,
    getAdminGameEvents,
    createAdminGameEvent,
    updateAdminGameEvent,
    announceGameEvent,
    adminAirdropBalance,
    getAdminRedemptions,
    updateRedemptionStatus,
  } = React.useMemo(() => createAdminApi<User>({
    getToken: () => token,
    setUser,
    setInvestigation,
    saveToken,
    clearToken,
    saveInvestigateOriginToken,
    getInvestigateOriginToken,
    clearInvestigateOriginToken,
  }), [
    clearInvestigateOriginToken,
    clearToken,
    getInvestigateOriginToken,
    saveInvestigateOriginToken,
    saveToken,
    token,
  ])

  const {
    getActiveSlotSettings,
    getLeaderboard,
    getAgencyStats,
    getAmaEvents,
    getStoreProducts,
    getMyStoreOrders,
    mockStoreCheckout,
    getGameEvents,
    getGameEventDetail,
    getUserPendingAirdrops,
    claimAirdrop,
  } = React.useMemo(() => createDashboardApi({
    getToken: () => token,
  }), [token])

  const {
    getAdminStreaks,
    createAdminStreak,
    updateAdminStreak,
    deleteAdminStreak,
    getAdminStreakAnalytics,
    getUserStreaks,
    claimStreak,
    getStreakHistory,
    markStreakSeen,
    getWeeklyRaffleStatus,
  } = createStreaksApi<User>({
    getToken: () => token,
    setUser,
  })

  const {
    getProfilePicturePresignedUrl,
    updateProfilePicture,
    getPaymentMethods,
    createRedemption,
    getUserRedemptions,
  } = createProfileApi<User>({
    getToken: () => token,
    setUser,
    refreshUser,
  })

  const getPokerRooms = useCallback(async () => {
    if (!token) return { success: false, message: "Not authenticated" }
    try {
      const response = await fetch("/api/poker/rooms", { headers: { Authorization: `Bearer ${token}` } })
      const data = await response.json()
      return { success: data.success, rooms: data.rooms, message: data.message }
    } catch (error) {
      console.error("Get poker rooms error:", error)
      return { success: false, message: "Network error" }
    }
  }, [token])

  const createPokerRoom = useCallback(async (name: string, settings: {
    maxPlayers?: number
    smallBlind: number
    bigBlind: number
    minBuyIn: number
    maxBuyIn: number
  }) => {
    if (!token) return { success: false, message: "Not authenticated" }
    try {
      const response = await fetch("/api/poker/rooms", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, settings }),
      })
      const data = await response.json()
      if (data.success) {
        await refreshUser()
      }
      return { success: data.success, room: data.room, buyIn: data.buyIn, message: data.message }
    } catch (error) {
      console.error("Create poker room error:", error)
      return { success: false, message: "Network error" }
    }
  }, [token, refreshUser])

  const joinPokerRoom = useCallback(async (roomId: string) => {
    if (!token) return { success: false, message: "Not authenticated" }
    try {
      const response = await fetch("/api/poker/rooms/join", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ roomId }),
      })
      const data = await response.json()
      if (data.success) {
        await refreshUser()
      }
      return { success: data.success, buyIn: data.buyIn, message: data.message }
    } catch (error) {
      console.error("Join poker room error:", error)
      return { success: false, message: "Network error" }
    }
  }, [token, refreshUser])

  const leavePokerRoom = useCallback(async (roomId: string) => {
    if (!token) return { success: false, message: "Not authenticated" }
    try {
      const response = await fetch("/api/poker/rooms/leave", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ roomId }),
      })
      const data = await response.json()
      if (data.success) {
        await refreshUser()
      }
      return { success: data.success, returnedChips: data.returnedChips, message: data.message }
    } catch (error) {
      console.error("Leave poker room error:", error)
      return { success: false, message: "Network error" }
    }
  }, [token, refreshUser])

  const addPokerBot = useCallback(async (roomId: string, difficulty: string) => {
    if (!token) return { success: false, message: "Not authenticated" }
    try {
      const response = await fetch("/api/poker/rooms/bots", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ roomId, difficulty }),
      })
      return await response.json()
    } catch (error) {
      console.error("Add poker bot error:", error)
      return { success: false, message: "Network error" }
    }
  }, [token])

  const removePokerBot = useCallback(async (roomId: string, seatIndex: number) => {
    if (!token) return { success: false, message: "Not authenticated" }
    try {
      const response = await fetch("/api/poker/rooms/bots/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ roomId, seatIndex }),
      })
      return await response.json()
    } catch (error) {
      console.error("Remove poker bot error:", error)
      return { success: false, message: "Network error" }
    }
  }, [token])

  const value: AuthContextType = {
    user,
    token,
    investigation,
    isLoading,
    isAuthenticated: !!user,
    login,
    signup,
    verifyEmail,
    verifyPhone,
    resendVerificationCode,
    resendPhoneVerificationCode,
    logout,
    completeOnboarding,
    validateAgencyId,
    checkAgencyName,
    getWelcomeSpinConfig,
    spinWelcomeReward,
    getCasinoSlotConfig,
    getActiveSlotSettings,
    claimWelcomeReward,
    submitDeal,
    claimDealSlotReward,
    claimFreeSpinReward,
    getDeals,
    getAgencyAgents,
    getMessages,
    sendChatMessage,
    reactToChatMessage,
    editChatMessage,
    pinChatMessage,
    deleteChatMessage,
    muteChatUser,
    voteChatPoll,
    getAdminSession,
    getAdminAgencyTargets,
    searchAdminUsers,
    startInvestigateMode,
    endInvestigateMode,
    postAdminSystemChatMessage,
    getPresignedUrl,
    getLeaderboard,
    getAgencyStats,
    getAmaEvents,
    getStoreProducts,
    getMyStoreOrders,
    mockStoreCheckout,
    createAdminAmaEvent,
    getAdminStoreProducts,
    saveAdminStoreProduct,
    getAdminStreaks,
    createAdminStreak,
    updateAdminStreak,
    deleteAdminStreak,
    getAdminStreakAnalytics,
    getUserStreaks,
    claimStreak,
    getStreakHistory,
    markStreakSeen,

    getProfilePicturePresignedUrl,
    updateProfilePicture,
    refreshUser,
    getAdminOverview,
    getAdminFilterOptions,
    getAdminDealVolume,
    getAdminDealAnalytics,
    getAdminPendingDeals,
    approveOrRejectDeal,
    getAdminAgentPerformance,
    getAdminAgencyOverview,
    getAdminAgenciesPaginated,
    getAdminChatActivity,
    getAdminChatControl,
    setAdminChatControl,
    adjustAdminUserEconomics,
    getAdminEconomicsSettings,
    updateAdminEconomicsSettings,
    getRecentDealFeed,
    getPokerRooms,
    createPokerRoom,
    joinPokerRoom,
    leavePokerRoom,
    addPokerBot,
    removePokerBot,
    getWeeklyRaffleStatus,
    getGameEvents,
    getGameEventDetail,
    getAdminGameEvents,
    createAdminGameEvent,
    updateAdminGameEvent,
    announceGameEvent,
    adminAirdropBalance,
    getUserPendingAirdrops,
    claimAirdrop,
    getPaymentMethods,
    createRedemption,
    getUserRedemptions,
    getAdminRedemptions,
    updateRedemptionStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
